<template>
  <div class="el-tabs--border-card" style="padding:15px;height:calc(100vh - 80px)">
    <el-row class="condition-set-box mr-b10">
      <el-form ref="form" :inline="true" :model="formData" :rules="formDataRules">
        <el-form-item prop="ipaddress" label="IP 주소" class="mib-browser-form-item">
          <el-input v-model="formData.ipaddress" class="mr-r4" />
          <el-button @click="openSettingPopup">
            <i class="fas fa-cog" />
          </el-button>
        </el-form-item>
        <el-form-item prop="oid" label="OID" class="mib-browser-form-item oid-form-item">
          <el-input v-model="formData.oid" class="mr-r4" />
          <el-select v-model="formData.methodType">
            <el-option v-for="item in methodTypeList" :key="item.value" :label="item.name" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="clickSearchButton" :disabled="isLoading">검색</el-button>
        </el-form-item>
      </el-form>
    </el-row>
    <el-row :gutter="10" style="height:calc(100% - 64px)">
      <el-col :span="8" style="height:100%">
        <w-mib-browser-tree @set-oid="setOid"></w-mib-browser-tree>
      </el-col>
      <el-col :span="16" style="height:100%">
        <w-mib-browser-table :table-data="tableData" @set-table-data="setTableData" @set-oid="setOid" :is-loading="isLoading" />
      </el-col>
    </el-row>
    <w-connection-setting-popup ref="settingPopup" :ipaddress="formData.ipaddress" @set-ipaddress="setIpaddress" />
  </div>
</template>

<script>
import { isNil, isEmpty, cloneDeep, assign, forEach, has } from 'lodash';
import { validateIpv4 } from '@/utils/string';
import MibAPI from '@/api/mib';
import { createEncryptTargetData, getRsaEncrypt } from '@/utils/rsa-encrypt';
import WMibBrowserTree from './MibBrowserTree';
import WMibBrowserTable from './MibBrowserTable';
import WConnectionSettingPopup from '../popup/ConnectionSettingPopup';

export default {
  name: 'WMibBrowserPanel',
  components: {
    WMibBrowserTree,
    WMibBrowserTable,
    WConnectionSettingPopup,
  },
  props: {
    agentManagerId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isLoading: false,
      selectedObjInfo: null,
      formData: {
        ipaddress: null,
        oid: null,
        methodType: 0,
        snmpTrycount: 1,
        snmpTimeout: 500,
      },
      formDataRules: {
        ipaddress: {
          required: true,
          trigger: 'blur',
          validator: this.validateIpaddress,
        },
        oid: {
          required: true,
          trigger: 'blur',
          message: '필수 정보입니다',
        },
      },
      nextOid: null,
      tableData: [],
      methodTypeList: [
        {
          name: 'Get',
          value: 0,
        },
        {
          name: 'GetNext',
          value: 1,
        },
        {
          name: 'SubTree',
          value: 2,
        },
        {
          name: 'Walk',
          value: 5,
        },
      ],
    };
  },
  watch: {
    'formData.ipaddress': {
      handler() {
        this.$refs.form.validateField('ipaddress');
      },
    },
    'formData.oid': {
      handler() {
        this.$refs.form.validateField('oid');
      },
    },
  },
  mounted() {
    const objId = this.$route.params.objId;
    if (!isNil(objId)) {
      MibAPI.getObjInfo(objId).then((response) => {
        const result = response.data;
        if (result.success) {
          const objInfo = result.data;
          this.selectedObjInfo = objInfo;
          this.formData.ipaddress = objInfo.ipaddressIpv4;
          this.setPopupFormData();
        }
      });
    }
  },
  methods: {
    clickSearchButton() {
      this.$refs.form
        .validate()
        .then(() => {
          // 최초 팝업 오픈시 connectionSettingPopup의 form ref에 접근이 되지 않아 정보로 검색 가능한지 판단
          if (isNil(this.$refs.settingPopup.$refs.form)) {
            const objInfo = this.selectedObjInfo;
            if (isNil(objInfo)) {
              this.openPopupAndValidate();
            } else {
              /* eslint-disable no-lonely-if */
              if (isNil(objInfo.snmpPort) || isNil(objInfo.snmpVersion)) {
                this.openPopupAndValidate();
              } else {
                const snmpVersion = objInfo.snmpVersion;
                if (snmpVersion === 'V3') {
                  if (isNil(objInfo.snmpV3Username)) {
                    this.openPopupAndValidate();
                  } else {
                    this.loadMibData();
                  }
                } else {
                  if (isNil(objInfo.snmpCommunityRead)) {
                    this.openPopupAndValidate();
                  } else {
                    this.loadMibData();
                  }
                }
              }
              /* eslint-enable no-lonely-if */
            }
          } else {
            this.$refs.settingPopup.$refs.form
              .validate()
              .then(() => {
                this.loadMibData();
              })
              .catch(() => {
                this.openSettingPopup();
              });
          }
        })
        .catch(() => {});
    },
    loadMibData() {
      const mibParams = this.getMibParams();
      forEach(['telnetuserId1', 'telnetuserPw1', 'telnetuserId2', 'telnetuserPw2'], (field) => {
        if (has(mibParams, field)) {
          delete mibParams[field];
        }
      });
      const encryptTargetData = createEncryptTargetData(mibParams, ['snmpCommunityRead', 'snmpCommunityReadwrite', 'snmpV3AuthPassword', 'snmpV3PrivPassword']);
      getRsaEncrypt(encryptTargetData)
        .then((encryptData) => {
          this.isLoading = true;
          const params = assign(mibParams, encryptData);
          if (!isNil(this.selectedObjInfo)) {
            params.objId = this.selectedObjInfo.objId;
          }
          MibAPI.getOidList(params)
            .then((response) => {
              const result = response.data;
              if (result.success) {
                const isError = result.data[0].isError;
                if (isError) {
                  this.$message(result.data[0].value);
                } else {
                  const methodType = this.formData.methodType;
                  if (!isEmpty(result.data)) {
                    this.formData.oid = methodType === 1 ? result.data[result.data.length - 1].oid : this.formData.oid;
                    this.tableData = this.tableData.concat(result.data);
                  }
                }
              } else {
                this.$message('검색을 실패하였습니다.');
              }
            })
            .catch(() => {
              this.$message('통신이 원할하지 않습니다.');
            })
            .finally(() => {
              this.isLoading = false;
            });
        })
        .catch((e) => {
          this.$message('검색을 실패하였습니다.');
          throw new Error(e);
        });
    },
    setOid(oid) {
      this.formData.oid = oid;
    },
    openSettingPopup() {
      this.$refs.settingPopup.open();
    },
    getMibParams() {
      const formData = cloneDeep(this.formData);
      if (formData.methodType === 0 || formData.methodType === 5) {
        formData.oid = `${formData.oid}.0`;
      } else if (formData.methodType === 1) {
        formData.oid = isNil(this.nextOid) ? `${formData.oid}` : this.nextOid;
      }
      const mibParams = Object.assign(cloneDeep(this.$refs.settingPopup.formData), formData);
      mibParams.agentManagerId = this.agentManagerId;
      return mibParams;
    },
    setPopupFormData() {
      this.$refs.settingPopup.setFormData(this.selectedObjInfo);
    },
    validateIpaddress(rule, value, callback) {
      if (isNil(value) || value === '') {
        callback(new Error('필수 정보입니다'));
      } else if (!validateIpv4(value)) {
        callback(new Error('IP를 확인해주세요'));
      } else {
        callback();
      }
    },
    setTableData(tableData) {
      this.tableData = tableData;
    },
    setIpaddress(ipaddress) {
      this.formData.ipaddress = ipaddress;
    },
    openPopupAndValidate() {
      this.openSettingPopup();
      this.$nextTick(() => {
        this.$refs.settingPopup.$refs.form
          .validate()
          .then()
          .catch();
      });
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/popup/MibBrowser/components/MibBrowserPanel.vue